// Capabilities client for GET /api/hero/capabilities.
//
// One-shot fetch on widget mount. The BFF returns the feature list
// matching the auth state (Authorization: Bearer header presence).
// Caching is the BFF's responsibility (Cache-Control: private, max-age=300
// per T14); this client does not cache locally.
//
// Auth contract details (token format, error semantics) are formalized in
// T13 in docs/auth-contract.md. The widget falls back to anonymous mode
// on AuthError per the contract.

import type { CapabilitiesResponse } from '../types/index.js';
import { AuthError, NetworkError } from './errors.js';

export interface CapabilitiesClientOptions {
  apiBase?: string;
  authToken?: string;
  fetchImpl?: typeof fetch;
}

export class CapabilitiesClient {
  private apiBase: string;
  private authToken: string | undefined;
  private fetchImpl: typeof fetch;

  constructor(opts: CapabilitiesClientOptions = {}) {
    this.apiBase = opts.apiBase ?? '';
    this.authToken = opts.authToken;
    this.fetchImpl = opts.fetchImpl ?? globalThis.fetch.bind(globalThis);
  }

  setAuthToken(token: string | undefined): void {
    this.authToken = token;
  }

  /**
   * Fetch the feature list. Throws AuthError on 401/403 (caller falls
   * back to anonymous), NetworkError on other failures.
   */
  async fetch(signal?: AbortSignal): Promise<CapabilitiesResponse> {
    const url = `${this.apiBase}/api/hero/capabilities`;
    const headers: Record<string, string> = {};
    if (this.authToken) {
      headers['Authorization'] = `Bearer ${this.authToken}`;
    }

    let response: Response;
    try {
      response = await this.fetchImpl(url, { method: 'GET', headers, signal });
    } catch (e) {
      if (e instanceof Error && e.name === 'AbortError') throw e;
      throw new NetworkError(`fetch failed: ${(e as Error).message}`);
    }

    if (response.status === 401 || response.status === 403) {
      const body = await response.text().catch(() => '');
      throw new AuthError(
        `capabilities: ${response.status === 401 ? 'unauthorized' : 'forbidden'}`,
        response.status,
        body,
      );
    }
    if (!response.ok) {
      const body = await response.text().catch(() => '');
      throw new NetworkError(`capabilities: HTTP ${response.status}`, response.status, body);
    }
    try {
      return (await response.json()) as CapabilitiesResponse;
    } catch (e) {
      throw new NetworkError(`capabilities: failed to parse JSON: ${(e as Error).message}`);
    }
  }

  /**
   * Convenience: returns the anonymous-mode CapabilitiesResponse without
   * an HTTP request. Used by the root component (T12) as fallback when
   * AuthError or network failure prevents a real fetch — keeps the widget
   * usable rather than blank.
   */
  static anonymousFallback(): CapabilitiesResponse {
    return {
      features: ['signup_gate', 'signup_cta', 'landing_animations', 'allow_refine'],
      version: 'v1',
    };
  }
}
